import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { addressFormMessages } from './address-form-messages';
import { AutofillSelect } from './autofill-select';

import type { Country } from '../types/reference-data';

type CountryPickerProps = {
  value: string;
  onChange: (countryCode: string) => void;
  countries: Country[];
  isLoadingCountries: boolean;
  sellerHomeCountry: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  autocompleteSection?: string;
  'data-testid'?: string;
};

/**
 * Country selector with home-country sorting and autofill support.
 * Data (countries list, loading flag, sellerHomeCountry) is injected via
 * props so the component has no dependency on any consumer's state store
 * or data-fetching library.
 */
export const CountryPicker = ({
  value,
  onChange,
  countries,
  isLoadingCountries,
  sellerHomeCountry,
  required = false,
  disabled = false,
  error,
  autocompleteSection,
  'data-testid': testId,
}: CountryPickerProps) => {
  const intl = useIntl();

  const sortedCountries = useMemo(() => {
    return [...countries].sort((a, b) => {
      if (a.code === sellerHomeCountry) return -1;
      if (b.code === sellerHomeCountry) return 1;
      return a.name.localeCompare(b.name, intl.locale);
    });
  }, [countries, sellerHomeCountry, intl.locale]);

  const selectData = useMemo(() => sortedCountries.map((country) => ({ value: country.code, label: country.name })), [sortedCountries]);

  const label = intl.formatMessage(addressFormMessages.country);
  const autoComplete = autocompleteSection ? `${autocompleteSection} country-name` : 'country-name';

  const handleChange = (val: string | null) => {
    if (val) {
      onChange(val);
    }
  };

  return (
    <AutofillSelect
      autoComplete={autoComplete}
      data={selectData}
      data-testid={testId ?? 'country-select'}
      disabled={disabled || isLoadingCountries}
      error={error}
      label={label}
      onChange={handleChange}
      placeholder={
        isLoadingCountries
          ? intl.formatMessage(addressFormMessages.loadingCountries)
          : intl.formatMessage(addressFormMessages.selectCountry)
      }
      required={required}
      value={value}
    />
  );
};
